import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'

const KINDS = ['Trial', 'Payg', 'Monthly']

const blank = {
  code: '',
  name: '',
  kind: 'Monthly',
  includedCredits: 0,
  priceMkd: 0,
  rolloverMonths: 1,
  sortOrder: 0,
  isActive: true,
  reason: '',
}

/**
 * The packages, as a table with the four things one does to them.
 *
 * These prices live in the database because purchases point at them — unlike the model costs,
 * which stay in configuration with a test that fails when the two hosts disagree. Editing happens
 * in a form above the table rather than inside the row: eight fields wedged into a row is a form
 * with worse manners, and on a phone it is unusable.
 */
export default function Plans({ onExpired }) {
  const { t } = useTranslation()
  const [plans, setPlans] = useState([])
  const [editing, setEditing] = useState(null)
  const [removing, setRemoving] = useState(null)
  const [reason, setReason] = useState('')
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    api
      .plans()
      .then((result) => {
        setPlans(result)
        setError(null)
      })
      .catch((failure) => {
        onExpired(failure)
        setError(failure.message)
      })
  }, [onExpired])

  useEffect(load, [load])

  async function run(action, message) {
    setBusy(true)
    setError(null)
    setNotice(null)

    try {
      await action()
      setNotice(message)
      setEditing(null)
      setRemoving(null)
      setReason('')
      load()
    } catch (failure) {
      onExpired(failure)
      setError(failure.message)
    } finally {
      setBusy(false)
    }
  }

  const body = (plan) => ({
    code: plan.code,
    name: plan.name,
    kind: plan.kind,
    includedCredits: Number(plan.includedCredits),
    priceMkd: Number(plan.priceMkd),
    rolloverMonths: Number(plan.rolloverMonths),
    sortOrder: Number(plan.sortOrder),
    isActive: plan.isActive,
    reason: plan.reason,
  })

  const save = (plan) =>
    run(
      () => (plan.id ? api.editPlan(plan.id, body(plan)) : api.addPlan(body(plan))),
      t('plans.saved'),
    )

  // Switching one off is a one-field edit, so it asks for its reason and sends the rest unchanged.
  const toggle = (plan) =>
    setEditing({ ...plan, isActive: !plan.isActive, reason: '' })

  const set = (field) => (event) =>
    setEditing({
      ...editing,
      [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
    })

  return (
    <>
      <section className="card">
        <div className="row">
          <h2>{t('plans.title')}</h2>
          <button type="button" className="button" onClick={() => setEditing({ ...blank })}>
            {t('plans.add')}
          </button>
        </div>

        {error && <p className="status bad">{error}</p>}
        {notice && <p className="status good">{notice}</p>}
      </section>

      {editing && (
        <section className="card">
          <h2>{editing.id ? t('plans.editing', { name: editing.name }) : t('plans.add')}</h2>

          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault()
              save(editing)
            }}
          >
            <label>
              {t('plans.code')}
              <input required value={editing.code} onChange={set('code')} />
            </label>

            <label>
              {t('plans.name')}
              <input required value={editing.name} onChange={set('name')} />
            </label>

            <label>
              {t('plans.kind')}
              {/* Only when new: a package's shape must not change under purchases already made
                  against it. */}
              <select value={editing.kind} disabled={Boolean(editing.id)} onChange={set('kind')}>
                {KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {kind}
                  </option>
                ))}
              </select>
            </label>

            <div className="row wrap">
              <label className="grow">
                {t('plans.credits')}
                <input type="number" min="0" value={editing.includedCredits} onChange={set('includedCredits')} />
              </label>

              <label className="grow">
                {t('plans.price')}
                <input type="number" min="0" step="1" value={editing.priceMkd} onChange={set('priceMkd')} />
              </label>

              <label className="grow">
                {t('plans.rollover')}
                <input type="number" min="0" max="24" value={editing.rolloverMonths} onChange={set('rolloverMonths')} />
              </label>

              <label className="grow">
                {t('plans.order')}
                <input type="number" value={editing.sortOrder} onChange={set('sortOrder')} />
              </label>
            </div>

            <label className="inline">
              <input type="checkbox" checked={editing.isActive} onChange={set('isActive')} />
              {t('plans.active')}
            </label>

            <label>
              {t('plans.reason')}
              <input required minLength={5} value={editing.reason} onChange={set('reason')} />
            </label>

            <div className="row">
              <button type="submit" className="button" disabled={busy || editing.reason.length < 5}>
                {t('plans.save')}
              </button>
              <button type="button" className="button quiet" onClick={() => setEditing(null)}>
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </section>
      )}

      {removing && (
        <section className="card">
          <h2>{t('plans.removing', { name: removing.name })}</h2>
          <p className="muted small">{t('plans.removeHint')}</p>

          <form
            className="row"
            onSubmit={(event) => {
              event.preventDefault()
              run(() => api.deletePlan(removing.id, reason), t('plans.removed'))
            }}
          >
            <input
              required
              minLength={5}
              className="grow"
              placeholder={t('plans.reason')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <button type="submit" className="button danger" disabled={busy || reason.length < 5}>
              {t('plans.remove')}
            </button>
            <button type="button" className="button quiet" onClick={() => setRemoving(null)}>
              {t('common.cancel')}
            </button>
          </form>
        </section>
      )}

      <section className="card">
        <div className="scroll">
          <table className="crud">
            <thead>
              <tr>
                <th>{t('plans.code')}</th>
                <th>{t('plans.name')}</th>
                <th>{t('plans.kind')}</th>
                <th className="num">{t('plans.credits')}</th>
                <th className="num">{t('plans.price')}</th>
                <th className="num">{t('plans.perCredit')}</th>
                <th className="num">{t('plans.rollover')}</th>
                <th className="num">{t('plans.sold')}</th>
                <th>{t('plans.state')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id} className={plan.isActive ? '' : 'off'}>
                  <td data-label={t('plans.code')}>
                    <code>{plan.code}</code>
                  </td>
                  <td data-label={t('plans.name')}>{plan.name}</td>
                  <td data-label={t('plans.kind')} className="small muted">
                    {plan.kind}
                  </td>
                  <td data-label={t('plans.credits')} className="num">
                    {plan.includedCredits}
                  </td>
                  <td data-label={t('plans.price')} className="num">
                    {Math.round(plan.priceMkd)}
                  </td>
                  <td data-label={t('plans.perCredit')} className="num">
                    {plan.includedCredits > 0 ? (plan.priceMkd / plan.includedCredits).toFixed(1) : '—'}
                  </td>
                  <td data-label={t('plans.rollover')} className="num">
                    {plan.rolloverMonths}
                  </td>
                  <td data-label={t('plans.sold')} className="num">
                    {plan.purchases}
                  </td>
                  <td data-label={t('plans.state')}>
                    <span className={`tag ${plan.isActive ? '' : 'warn'}`}>
                      {t(plan.isActive ? 'plans.on' : 'plans.offState')}
                    </span>
                  </td>
                  <td className="actions">
                    <button type="button" className="button quiet" onClick={() => setEditing({ ...plan, reason: '' })}>
                      {t('plans.edit')}
                    </button>
                    <button type="button" className="button quiet" onClick={() => toggle(plan)}>
                      {t(plan.isActive ? 'plans.switchOff' : 'plans.switchOn')}
                    </button>
                    <button
                      type="button"
                      className="button danger"
                      disabled={plan.purchases > 0}
                      title={plan.purchases > 0 ? t('plans.removeHint') : undefined}
                      onClick={() => {
                        setRemoving(plan)
                        setReason('')
                      }}
                    >
                      {t('plans.remove')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {plans.length === 0 && <p className="muted">{t('plans.empty')}</p>}
      </section>
    </>
  )
}
